/**
 * editMutationsV7.js — fillet, chamfer, extend, polygon
 *
 * These close the gap vs AutoCAD in 2D editing.
 * All use the existing DCEL + primitives systems.
 */

import { Geo } from "../utils/geo";
import { mkId } from "../utils/coords";
import { mutAddArc, mutAddCircle } from "./primitives";

const EPS = 1e-9;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function edgeVec(eId, st) {
  const e = st.edges[eId]; if (!e) return null;
  const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
  if (!ab || !ba) return null;
  const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
  if (!nA || !nB) return null;
  return { nA, nB, aId: ab.originId, bId: ba.originId, len: Geo.d(nA, nB) };
}

// ─── FILLET ───────────────────────────────────────────────────────────────────

/**
 * mutFillet(eIdA, eIdB, radius, st)
 *
 * Rounds the corner between two connected edges with an arc of given radius.
 * The shared node is trimmed back and the arc inserted.
 *
 * Steps:
 *  1. Find shared node between the two edges
 *  2. Compute tangent points (radius from corner along each edge)
 *  3. Compute arc center (equidistant from both tangent points at radius)
 *  4. Move shared node endpoints back to tangent points
 *  5. Add arc primitive connecting the tangent points
 */
export function mutFillet(eIdA, eIdB, radius, st) {
  if (radius <= 0) return st;

  const ea = edgeVec(eIdA, st), eb = edgeVec(eIdB, st);
  if (!ea || !eb) return st;

  // Find shared node
  let sharedId = null, ptA = null, ptB = null; // ptA = far end of ea, ptB = far end of eb
  if (ea.aId === eb.aId) { sharedId = ea.aId; ptA = ea.nB; ptB = eb.nB; }
  else if (ea.aId === eb.bId) { sharedId = ea.aId; ptA = ea.nB; ptB = eb.nA; }
  else if (ea.bId === eb.aId) { sharedId = ea.bId; ptA = ea.nA; ptB = eb.nB; }
  else if (ea.bId === eb.bId) { sharedId = ea.bId; ptA = ea.nA; ptB = eb.nA; }
  if (!sharedId) return st; // not connected

  const corner = st.nodes[sharedId];

  // Unit vectors from corner along each edge
  const dA = { x: ptA.x - corner.x, y: ptA.y - corner.y };
  const dB = { x: ptB.x - corner.x, y: ptB.y - corner.y };
  const lA = Math.sqrt(dA.x*dA.x + dA.y*dA.y) || 1;
  const lB = Math.sqrt(dB.x*dB.x + dB.y*dB.y) || 1;
  const uA = { x: dA.x/lA, y: dA.y/lA };
  const uB = { x: dB.x/lB, y: dB.y/lB };

  // Half-angle between edges
  const dot = Math.max(-1, Math.min(1, uA.x*uB.x + uA.y*uB.y));
  const halfAngle = Math.acos(dot) / 2;
  if (halfAngle < EPS) return st; // collinear edges

  // Distance from corner to tangent points
  const tanDist = radius / Math.tan(halfAngle);
  if (tanDist > lA || tanDist > lB) return st; // radius too large

  // Tangent points
  const tA = { x: corner.x + uA.x * tanDist, y: corner.y + uA.y * tanDist };
  const tB = { x: corner.x + uB.x * tanDist, y: corner.y + uB.y * tanDist };

  // Arc center: perpendicular from each tangent point inward
  // The center is at distance=radius from both tangent points
  const midX = (tA.x + tB.x) / 2, midY = (tA.y + tB.y) / 2;
  const perpX = -(tB.y - tA.y), perpY = tB.x - tA.x;
  const perpLen = Math.sqrt(perpX*perpX + perpY*perpY) || 1;
  const distToCenter = Math.sqrt(Math.max(0, radius*radius - ((tB.x-tA.x)**2 + (tB.y-tA.y)**2)/4));
  // Pick direction: toward corner
  const toCornerX = corner.x - midX, toCornerY = corner.y - midY;
  const dot2 = toCornerX * perpX + toCornerY * perpY;
  const sign = dot2 > 0 ? -1 : 1;
  const cx = midX + sign * (perpX/perpLen) * distToCenter;
  const cy = midY + sign * (perpY/perpLen) * distToCenter;

  const startAngle = Math.atan2(tA.y - cy, tA.x - cx);
  const endAngle   = Math.atan2(tB.y - cy, tB.x - cx);

  // Move shared node endpoint of ea to tA, eb to tB
  const newNodes = { ...st.nodes };
  newNodes[sharedId] = { ...corner, x: Math.round(tA.x), y: Math.round(tA.y) };

  // Add the arc
  let s2 = mutAddArc(cx, cy, radius, startAngle, endAngle, "walls", { ...st, nodes: newNodes });

  return s2;
}

// ─── CHAMFER ──────────────────────────────────────────────────────────────────

/**
 * mutChamfer(eIdA, eIdB, dist1, dist2, st)
 *
 * Cuts the corner between two edges at distances dist1 and dist2 from the corner.
 * Creates a diagonal line replacing the sharp corner.
 */
export function mutChamfer(eIdA, eIdB, dist1, dist2, st) {
  const ea = edgeVec(eIdA, st), eb = edgeVec(eIdB, st);
  if (!ea || !eb) return st;

  let sharedId = null, ptA = null, ptB = null;
  if (ea.aId === eb.aId)      { sharedId=ea.aId; ptA=ea.nB; ptB=eb.nB; }
  else if (ea.aId === eb.bId) { sharedId=ea.aId; ptA=ea.nB; ptB=eb.nA; }
  else if (ea.bId === eb.aId) { sharedId=ea.bId; ptA=ea.nA; ptB=eb.nB; }
  else if (ea.bId === eb.bId) { sharedId=ea.bId; ptA=ea.nA; ptB=eb.nA; }
  if (!sharedId) return st;

  const corner = st.nodes[sharedId];
  const dA = { x: ptA.x-corner.x, y: ptA.y-corner.y };
  const dB = { x: ptB.x-corner.x, y: ptB.y-corner.y };
  const lA = Math.sqrt(dA.x*dA.x+dA.y*dA.y) || 1;
  const lB = Math.sqrt(dB.x*dB.x+dB.y*dB.y) || 1;

  const d1 = Math.min(dist1, lA * 0.9);
  const d2 = Math.min(dist2 ?? dist1, lB * 0.9);

  const cA = { x: corner.x + dA.x/lA*d1, y: corner.y + dA.y/lA*d1 };
  const cB = { x: corner.x + dB.x/lB*d2, y: corner.y + dB.y/lB*d2 };

  // Move corner node to cA, add new node at cB, add chamfer line
  const newNodeId = mkId("n");
  const newNodes = {
    ...st.nodes,
    [sharedId]:  { ...corner, x: Math.round(cA.x), y: Math.round(cA.y) },
    [newNodeId]: { id: newNodeId, x: Math.round(cB.x), y: Math.round(cB.y), halfEdgeIds: [] },
  };

  // Add the chamfer edge (line cA → cB)
  // We use the wall mutation system directly
  const heAB = mkId("he"), heBA = mkId("he"), eId = mkId("e");
  const newHE = {
    ...st.halfEdges,
    [heAB]: { id:heAB, originId:sharedId,  twinId:heBA, nextId:null, faceId:null },
    [heBA]: { id:heBA, originId:newNodeId, twinId:heAB, nextId:null, faceId:null },
  };
  const newEdges = {
    ...st.edges,
    [eId]: { id:eId, halfEdgeA:heAB, halfEdgeB:heBA, thickness:ea.nA ? st.edges[eIdA]?.thickness || 18 : 18, layerId:"walls" },
  };
  newNodes[sharedId].halfEdgeIds  = [...(newNodes[sharedId].halfEdgeIds||[]),  heAB];
  newNodes[newNodeId].halfEdgeIds = [...(newNodes[newNodeId].halfEdgeIds||[]), heBA];

  return { ...st, nodes:newNodes, halfEdges:newHE, edges:newEdges };
}

// ─── EXTEND ───────────────────────────────────────────────────────────────────

/**
 * mutExtend(eId, boundaryEId, st)
 *
 * Extends an edge until it meets a boundary edge.
 * The nearest endpoint of eId is moved to the intersection point.
 */
export function mutExtend(eId, boundaryEId, st) {
  const e  = edgeVec(eId, st);
  const eb = edgeVec(boundaryEId, st);
  if (!e || !eb) return st;

  // Extend the line of e until it hits the line of eb
  // Use parametric intersection
  const ix = Geo.sx(e.nA, e.nB, eb.nA, eb.nB);
  if (!ix) return st; // parallel

  // Determine which end of e to move: whichever is further from ix
  const dA = Geo.d(e.nA, ix), dB = Geo.d(e.nB, ix);
  const moveId = dA > dB ? e.aId : e.bId;

  const newNodes = {
    ...st.nodes,
    [moveId]: { ...st.nodes[moveId], x: Math.round(ix.x), y: Math.round(ix.y) },
  };
  return { ...st, nodes: newNodes };
}

// ─── POLYGON (N-sided regular) ────────────────────────────────────────────────

/**
 * mutAddPolygon(cx, cy, r, sides, rotation, layerId, st)
 *
 * Adds a regular N-sided polygon as a polyline primitive.
 * r = circumscribed radius (center to vertex)
 * rotation = rotation in degrees (0 = first vertex at right)
 */
export function mutAddPolygon(cx, cy, r, sides, rotation = 0, layerId, st) {
  if (sides < 3 || r < EPS) return st;
  const rotRad = rotation * Math.PI / 180;
  const pts = [];
  for (let i = 0; i < sides; i++) {
    const a = (2 * Math.PI * i / sides) + rotRad;
    pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }

  const id = mkId("pl");
  const poly = { id, layerId: layerId || "walls", pts, closed: true, filled: false };
  return { ...st, polylines: { ...(st.polylines || {}), [id]: poly } };
}

// ─── BREAK (split edge at point) ─────────────────────────────────────────────

/**
 * mutBreakEdge(eId, pt, st)
 *
 * Splits an edge at the given point (snapped to the edge).
 * Creates two edges sharing the new intermediate node.
 */
export function mutBreakEdge(eId, pt, st) {
  const ev = edgeVec(eId, st); if (!ev) return st;

  const proj = Geo.project(pt, ev.nA, ev.nB);
  const snap = proj.point;
  if (Geo.d(snap, ev.nA) < 1 || Geo.d(snap, ev.nB) < 1) return st; // too close to endpoint

  // New mid node
  const midId = mkId("n");
  const e = st.edges[eId];
  const he1Id = mkId("he"), he2Id = mkId("he");
  const he3Id = mkId("he"), he4Id = mkId("he");
  const eAId = mkId("e"), eBId = mkId("e");

  const nodes = {
    ...st.nodes,
    [midId]: { id:midId, x:Math.round(snap.x), y:Math.round(snap.y), halfEdgeIds:[he1Id,he4Id] },
  };
  const halfEdges = { ...st.halfEdges };
  // Remove old HEs
  delete halfEdges[e.halfEdgeA]; delete halfEdges[e.halfEdgeB];

  halfEdges[he1Id] = { id:he1Id, originId:ev.aId, twinId:he2Id, nextId:null, faceId:null };
  halfEdges[he2Id] = { id:he2Id, originId:midId,  twinId:he1Id, nextId:null, faceId:null };
  halfEdges[he3Id] = { id:he3Id, originId:midId,  twinId:he4Id, nextId:null, faceId:null };
  halfEdges[he4Id] = { id:he4Id, originId:ev.bId, twinId:he3Id, nextId:null, faceId:null };

  const edges = { ...st.edges };
  delete edges[eId];
  edges[eAId] = { ...e, id:eAId, halfEdgeA:he1Id, halfEdgeB:he2Id };
  edges[eBId] = { ...e, id:eBId, halfEdgeA:he3Id, halfEdgeB:he4Id };

  const nn = { ...nodes };
  nn[ev.aId] = { ...nn[ev.aId], halfEdgeIds: [...(nn[ev.aId].halfEdgeIds||[]).filter(id=>id!==e.halfEdgeA), he1Id] };
  nn[ev.bId] = { ...nn[ev.bId], halfEdgeIds: [...(nn[ev.bId].halfEdgeIds||[]).filter(id=>id!==e.halfEdgeB), he4Id] };

  return { ...st, nodes:nn, halfEdges, edges };
}

// ─── LINETYPE / LINEWEIGHT assignment ─────────────────────────────────────────

export function mutSetLinetype(entityType, entityId, linetype, st) {
  // entityType: "edge" | "circle" | "arc" | "spline" | "polyline"
  const key = entityType === "edge" ? "edges" : entityType + "s";
  const entity = (st[key] || {})[entityId]; if (!entity) return st;
  return { ...st, [key]: { ...st[key], [entityId]: { ...entity, linetype } } };
}

export function mutSetLineweight(entityType, entityId, lineweight, st) {
  const key = entityType === "edge" ? "edges" : entityType + "s";
  const entity = (st[key] || {})[entityId]; if (!entity) return st;
  return { ...st, [key]: { ...st[key], [entityId]: { ...entity, lineweight } } };
}

export function mutSetEntityColor(entityType, entityId, color, st) {
  const key = entityType === "edge" ? "edges" : entityType + "s";
  const entity = (st[key] || {})[entityId]; if (!entity) return st;
  return { ...st, [key]: { ...st[key], [entityId]: { ...entity, color } } };
}

// ─── PANEL label ─────────────────────────────────────────────────────────────

export function mutSetPanelProps(eId, props, st) {
  // props: { panelType, label, edgeBanding, materialId }
  const e = st.edges[eId]; if (!e) return st;
  return { ...st, edges: { ...st.edges, [eId]: { ...e, ...props } } };
}
